import FieldError from 'components/field-error/field-error';
import { ProPersonSearchFilter } from 'components/pro-contact-search-filter';
import { getIn, useFormikContext } from 'formik';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {
  Button,
  Checkbox,
  Container,
  Icon,
  Loader,
  Message,
  Table,
} from 'semantic-ui-react';
import {
  PhoneNumber,
  ToastError,
  ToastInfo,
  ToastSuccess,
  ToastWarn,
} from 'utils';
import { ProContactDeleteConfirmModal } from './pro-contact-delete-confirm-modal';
import ContactFormModal from 'components/contact-form-modal/contact-form-modal';
import { useAddContactToView } from 'generated/api-client/@tanstack/react-query.gen';
import {
  ProContactsFieldQuery,
  useProContactsFieldQuery,
} from 'generated/graphql';

type ModalState = {
  open: boolean;
  id?: number;
  email?: string;
};

type ProContactsFieldProps = {
  name: string;
  currentProViewId?: number;
  onProContactsLoaded?: (
    proContacts: ProContactsFieldQuery['proPerson'],
  ) => void;
};

const ProContactsField: FunctionComponent<ProContactsFieldProps> = ({
  name,
  onProContactsLoaded,
  currentProViewId,
}) => {
  const {
    setFieldValue,
    setFieldTouched,
    values,
    initialValues,
    errors,
    touched,
  } = useFormikContext();

  const proContactsValues = useMemo(() => getIn(values, name), [values, name]);

  const proContactsIds = useMemo(
    () => proContactsValues.map((contact) => contact.id),
    [proContactsValues],
  );

  const { data, loading, refetch } = useProContactsFieldQuery({
    variables: { ids: proContactsIds },
    skip: proContactsIds.length === 0,
  });

  const proContacts = data?.proPerson ?? [];

  useEffect(() => {
    if (proContacts.length > 0 && onProContactsLoaded) {
      onProContactsLoaded(proContacts);
    }
  }, [proContacts]);

  const [contactModal, setContactModal] = useState<ModalState>({
    open: false,
  });

  const [proContactDeleteConfirmModal, setProContactDeleteConfirmModal] =
    useState<ModalState>({
      open: false,
    });

  const { mutateAsync: addContactToView } = useAddContactToView();

  const handleLinkProContact = useCallback(
    async (contactId: number) => {
      // Check if contact already exist, is so, do nothing and warn user
      if (proContactsValues.some((person) => person.id === contactId)) {
        ToastWarn('Attention', 'Contact déjà présent sur la fiche');
        return;
      }

      // Removed contacts stay in database until the pro view is saved
      const position =
        Math.max(
          0,
          ...[...proContactsValues, ...(getIn(initialValues, name) ?? [])].map(
            (contact) => contact.position,
          ),
        ) + 1;

      if (currentProViewId) {
        try {
          await addContactToView({
            path: {
              viewId: currentProViewId,
              contactId: contactId,
            },
            body: {
              position,
              isVisible: true,
            },
          });
          ToastSuccess('Succès', 'Contact enregistré sur la fiche');
        } catch (error) {
          console.log(error);
          ToastError(
            'Erreur',
            "Impossible d'enregistrer le contact sur la fiche",
          );
          return;
        }
      }

      setFieldTouched(name);
      setFieldValue(
        name,
        [
          ...proContactsValues,
          {
            id: contactId,
            position,
            isVisible: true,
          },
        ],
        true,
      );
    },
    [
      proContactsValues,
      initialValues,
      name,
      currentProViewId,
      setFieldTouched,
      setFieldValue,
    ],
  );

  if (loading) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  if (!proContacts) {
    return (
      <Message>
        Impossible de charger les contacts. Veuillez réessayer plus tard.
      </Message>
    );
  }

  return (
    <>
      {proContacts.length > 0 ? (
        <SortableTable
          name={name}
          setContactModal={setContactModal}
          setProContactDeleteConfirmModal={setProContactDeleteConfirmModal}
          proContactDeleteConfirmModal={proContactDeleteConfirmModal}
          contactModal={contactModal}
          proContacts={proContacts}
          items={proContactsValues}
          onSortEnd={({ oldIndex, newIndex }): void => {
            const newValues = arrayMove(
              proContactsValues,
              oldIndex,
              newIndex,
            ).map((contact: any, index) => {
              return {
                id: contact.id,
                isVisible: contact.isVisible,
                position: index + 1,
              };
            });
            setFieldTouched(name);
            setFieldValue(name, newValues, true);
          }}
          useDragHandle
          useWindowAsScrollContainer
        />
      ) : (
        <Message>Aucun contact</Message>
      )}
      <Container fluid textAlign="right">
        <ProPersonSearchFilter
          style={{ marginRight: '15px' }}
          placeholder="Nom, email..."
          onResultSelect={(_, { result }): void => {
            handleLinkProContact(result.value);
          }}
        />
        <Button
          positive
          icon
          type="button"
          style={{ minWidth: '45px', minHeight: '32px' }}
          onClick={(): void => {
            setContactModal({ open: true });
          }}
        >
          <Icon name="plus" />
        </Button>
      </Container>
      {errors[name] && touched[name] ? (
        <FieldError>{errors[name]}</FieldError>
      ) : null}
      <ContactFormModal
        open={contactModal.open}
        contactId={contactModal.id}
        onClose={(): void => {
          setContactModal({ open: false });
        }}
        onContactCreated={handleLinkProContact}
        onContactUpdated={(): void => {
          ToastInfo(
            'Info',
            "Pense à mettre à jour les accès à l'espace pro si nécessaire (changement d'email)!",
          );
          refetch();
        }}
      />
      {proContactDeleteConfirmModal.id ? (
        <ProContactDeleteConfirmModal
          open={proContactDeleteConfirmModal.open}
          onClose={(): void => {
            setProContactDeleteConfirmModal({ open: false });
          }}
          onConfirm={(contactId): void => {
            setFieldTouched(name);
            setFieldValue(
              name,
              proContactsValues.filter((contact) => contact.id !== contactId),
              true,
            );
            ToastInfo(
              'Contact retiré',
              currentProViewId ? 'Pense à enregistrer la fiche' : null,
            );
          }}
          contactId={proContactDeleteConfirmModal.id}
          contactEmail={proContactDeleteConfirmModal.email}
          currentProViewId={currentProViewId}
        />
      ) : null}
    </>
  );
};

type SortableTableProps = {
  proContacts;
  name;
  items;
  setContactModal;
  setProContactDeleteConfirmModal;
  proContactDeleteConfirmModal;
  contactModal;
};

const SortableTable = SortableContainer<SortableTableProps>(
  ({
    name,
    items,
    proContacts,
    setContactModal,
    setProContactDeleteConfirmModal,
    proContactDeleteConfirmModal,
    contactModal,
  }) => {
    return (
      <Table basic="very" selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign="center">#</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">Visible</Table.HeaderCell>
            <Table.HeaderCell>Prénom</Table.HeaderCell>
            <Table.HeaderCell>Nom</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Téléphone</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Action</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map(({ id, isVisible }, index) => {
            const contact = proContacts.find((contact) => contact.id === id);

            if (!contact) {
              return;
            }

            return (
              <SortableItem
                key={`pro-person-field-${contact.id}`}
                name={name}
                items={items}
                index={index}
                isVisible={isVisible}
                contact={contact}
                proContactDeleteConfirmModal={proContactDeleteConfirmModal}
                contactModal={contactModal}
                setContactModal={setContactModal}
                setProContactDeleteConfirmModal={
                  setProContactDeleteConfirmModal
                }
              />
            );
          })}
        </Table.Body>
      </Table>
    );
  },
);

interface SortableItemProps {
  items;
  name;
  contact;
  isVisible;
  setContactModal;
  proContactDeleteConfirmModal;
  contactModal;
  setProContactDeleteConfirmModal;
}

const SortableItem = SortableElement<SortableItemProps>(
  ({
    items,
    name,
    contact,
    isVisible,
    setContactModal,
    proContactDeleteConfirmModal,
    contactModal,
    setProContactDeleteConfirmModal,
  }) => {
    const { setFieldValue, setFieldTouched } = useFormikContext();

    return (
      <Table.Row>
        <DragHandle />
        <Table.Cell textAlign="center">
          <Checkbox
            checked={isVisible}
            onChange={(event, { checked }): void => {
              setFieldTouched(name, true);
              setFieldValue(
                name,
                items.map((item) => {
                  if (item.id === contact.id) {
                    item.isVisible = checked;
                  }
                  return item;
                }),
                true,
              );
            }}
          />
        </Table.Cell>
        <Table.Cell>{contact.givenName}</Table.Cell>
        <Table.Cell>{contact.familyName}</Table.Cell>
        <Table.Cell>{contact.email}</Table.Cell>
        <Table.Cell>
          {contact.telephone ? PhoneNumber.format(contact.telephone) : null}
        </Table.Cell>
        <Table.Cell textAlign="right">
          <Button
            icon
            negative
            type="button"
            basic
            style={{ minWidth: '35px', minHeight: '32px' }}
            loading={proContactDeleteConfirmModal.open}
            onClick={(): void => {
              setProContactDeleteConfirmModal({
                open: true,
                id: contact.id,
                email: contact.email,
              });
            }}
          >
            <Icon name="trash alternate outline" />
          </Button>
          <Button
            icon
            primary
            style={{ minWidth: '35px', minHeight: '32px' }}
            type="button"
            loading={contactModal.open}
            onClick={(): void => {
              setContactModal({ open: true, id: contact.id });
            }}
          >
            <Icon name="pencil alternate" />
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  },
);

const DragHandle = SortableHandle(() => (
  <Table.Cell textAlign="center">
    <Icon name="bars" />
  </Table.Cell>
));

export default ProContactsField;
